import React, { createContext, useContext, useReducer, useState } from 'react';
import { TwilioError } from 'twilio-video';
import { RoomType } from './types';
import { settingsReducer, initialSettings, Settings, SettingsAction } from './settingsReducer';
import useActiveSinkId from './useActiveSinkId';
import { useLocalStorageState } from '@/hooks/video/useLocalStorageState';
import { useAppDispatch } from '@/hooks';
import { getAccessToken, getRoomHasParticipants } from '@/store/slices/videoSlice';

export interface StateContextType {
  error: TwilioError | Error | null;
  setError(error: TwilioError | Error | null): void;
  getToken(roomName: string): Promise<any>;
  roomHasParticipants(roomName: string): Promise<any>;
  signIn?(passcode?: string): Promise<void>;
  signOut?(): Promise<void>;
  isAuthReady?: boolean;
  isFetching: boolean;
  activeSinkId: string;
  setActiveSinkId(sinkId: string): void;
  settings: Settings;
  dispatchSetting: React.Dispatch<SettingsAction>;
  roomType?: RoomType;
  isGalleryViewActive: boolean;
  setIsGalleryViewActive: React.Dispatch<React.SetStateAction<boolean>>;
  maxGalleryViewParticipants: number;
  setMaxGalleryViewParticipants: React.Dispatch<React.SetStateAction<number>>;
  isKrispEnabled: boolean;
  setIsKrispEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isKrispInstalled: boolean;
  setIsKrispInstalled: React.Dispatch<React.SetStateAction<boolean>>;
}

export const StateContext = createContext<StateContextType>(null!);

export default function VideoStateProvider(props: React.PropsWithChildren<any>): React.ReactElement {
  const [error, setError] = useState<TwilioError | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isGalleryViewActive, setIsGalleryViewActive] = useLocalStorageState('gallery-view-active-key', true);
  const [activeSinkId, setActiveSinkId] = useActiveSinkId();
  const [settings, dispatchSetting] = useReducer(settingsReducer, initialSettings);
  const [roomType, setRoomType] = useState<RoomType>();
  const [maxGalleryViewParticipants, setMaxGalleryViewParticipants] = useLocalStorageState(
    'max-gallery-participants-key',
    6,
  );
  const dispatch = useAppDispatch();

  const [isKrispEnabled, setIsKrispEnabled] = useState(false);
  const [isKrispInstalled, setIsKrispInstalled] = useState(false);

  let contextValue = {
    error,
    setError,
    isFetching,
    activeSinkId,
    setActiveSinkId,
    settings,
    dispatchSetting,
    roomType,
    isGalleryViewActive,
    setIsGalleryViewActive,
    maxGalleryViewParticipants,
    setMaxGalleryViewParticipants,
    isKrispEnabled,
    setIsKrispEnabled,
    isKrispInstalled,
    setIsKrispInstalled,
  } as StateContextType;

  contextValue = {
    ...contextValue,
    getToken: async roomName => {
      return dispatch(getAccessToken(+roomName));
    },
    roomHasParticipants: async roomName => {
      return dispatch(getRoomHasParticipants(+roomName));
    },
  };

  const getToken: StateContextType['getToken'] = roomName => {
    setIsFetching(true);

    return contextValue
      .getToken(roomName)
      .then(res => {
        setRoomType('peer-to-peer');
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const roomHasParticipants: StateContextType['roomHasParticipants'] = roomName => {
    return contextValue
      .roomHasParticipants(roomName)
      .then(res => {
        return res;
      })
      .catch(err => {
        return Promise.reject(err);
      });
  };

  return (
    <StateContext.Provider value={{ ...contextValue, getToken, roomHasParticipants }}>
      {props.children}
    </StateContext.Provider>
  );
}

export function useAppState(): StateContextType {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the VideoStateProvider');
  }
  return context;
}
